"use client";

import { useId, useRef, useState } from "react";
import { FiMessageSquare, FiX } from "react-icons/fi";

import { Icd10CodePicker } from "@/components/billing/icd10-search-select";
import { Textarea } from "@/components/ui";
import Button from "@/components/ui/button";

import { useIcd10Descriptions } from "@/hooks/use-icd10-descriptions";

import type { Icd10Answer } from "@/lib/icd10-codes-field";
import { cx } from "@/lib/tailwind-utils";

const Icd10CodeRows = ({
  codes,
  comments,
  onRemove,
  onCommentChange,
}: {
  codes: string[];
  comments: Record<string, string>;
  onRemove?: (code: string) => void;
  onCommentChange?: (code: string, comment: string) => void;
}) => {
  const descriptions = useIcd10Descriptions(codes);
  const idPrefix = useId();
  // Codes whose box a provider opened. A code with a comment always shows its
  // box, so the list only grows a box where a provider asked for one.
  const [openCodes, setOpenCodes] = useState<ReadonlySet<string>>(new Set());
  const commentBoxes = useRef(new Map<string, HTMLTextAreaElement>());
  const commentButtons = useRef(new Map<string, HTMLButtonElement>());

  const commentId = (code: string) => `${idPrefix}-comment-${code}`;
  const setOpen = (code: string, open: boolean) =>
    setOpenCodes((prev) => {
      const next = new Set(prev);
      if (open) next.add(code);
      else next.delete(code);
      return next;
    });
  // Focus goes to the code's comment button before the box unmounts; Safari
  // doesn't focus a clicked button, so it would otherwise fall to the page.
  const closeComment = (code: string) => {
    setOpen(code, false);
    commentButtons.current.get(code)?.focus();
  };
  // A closed box opens (and takes focus through autoFocus), an empty open one
  // closes, and one holding a comment takes focus.
  const toggleComment = (code: string, comment: string) => {
    const box = commentBoxes.current.get(code);
    if (!box) setOpen(code, true);
    else if (!comment) closeComment(code);
    else box.focus();
  };

  return (
    <ul className={onRemove || onCommentChange ? "space-y-3.5" : "space-y-2"}>
      {codes.map((code) => {
        const comment = comments[code] ?? "";
        const showEditor =
          !!onCommentChange && (openCodes.has(code) || comment !== "");
        const commentAction = comment ? "Edit" : showEditor ? "Close" : "Add";
        return (
          <li key={code}>
            <div className="flex items-start gap-3 text-body">
              <span className="w-20 shrink-0 font-mono text-foreground">
                {code}
              </span>
              <span className="flex-1 text-foreground-secondary">
                {descriptions[code.toUpperCase()]}
              </span>
              {(onCommentChange || onRemove) && (
                // 32px targets drawn into the 18px text line: the negative
                // margin keeps a row as tall as its text.
                <div className="-my-[7px] flex shrink-0 items-center gap-2">
                  {onCommentChange && (
                    <Button
                      ref={(button: HTMLButtonElement | null) => {
                        if (button) commentButtons.current.set(code, button);
                        else commentButtons.current.delete(code);
                      }}
                      variant="ghost"
                      size="icon-sm"
                      tooltip={`${commentAction} comment`}
                      aria-label={`${commentAction} comment for ${code}`}
                      aria-expanded={showEditor}
                      aria-controls={showEditor ? commentId(code) : undefined}
                      onClick={() => toggleComment(code, comment)}
                      className={cx(
                        !comment &&
                          "text-foreground-muted md:hover:text-foreground",
                      )}
                    >
                      <FiMessageSquare className="size-3.5" />
                    </Button>
                  )}
                  {onRemove && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      tooltip="Remove"
                      aria-label={`Remove ${code}`}
                      onClick={() => {
                        setOpen(code, false);
                        onRemove(code);
                      }}
                      className="text-foreground-muted md:hover:text-foreground"
                    >
                      <FiX className="size-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            {showEditor ? (
              // Full width, like the code search below: the note editor's
              // column is narrow even on a wide screen.
              <div className="mt-2">
                <Textarea
                  ref={(box: HTMLTextAreaElement | null) => {
                    if (box) commentBoxes.current.set(code, box);
                    else commentBoxes.current.delete(code);
                  }}
                  id={commentId(code)}
                  aria-label={`Comment on ${code}`}
                  autoFocus={!comment}
                  value={comment}
                  onChange={(e) => {
                    // A box shown for a saved comment must stay while that
                    // comment is cleared to be retyped.
                    if (!openCodes.has(code)) setOpen(code, true);
                    onCommentChange(code, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && !comment) closeComment(code);
                  }}
                  placeholder="e.g. Continue diet plan, recheck in 6 weeks"
                  rows={2}
                  className="max-h-40 min-h-[3.125rem] resize-y [field-sizing:content]"
                />
              </div>
            ) : comment.trim() ? (
              <p className="mt-0.5 whitespace-pre-wrap pl-[5.75rem] text-body text-foreground">
                {comment.trim()}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export const Icd10CodesInput = ({
  value: { codes, comments },
  onChange,
  disabled,
}: {
  value: Icd10Answer;
  onChange: (value: Icd10Answer) => void;
  disabled?: boolean;
}) => (
  <div className="space-y-2">
    {codes.length > 0 && (
      <Icd10CodeRows
        codes={codes}
        comments={comments}
        onRemove={
          disabled
            ? undefined
            : (code) =>
                onChange({ codes: codes.filter((c) => c !== code), comments })
        }
        onCommentChange={
          disabled
            ? undefined
            : (code, comment) =>
                onChange({ codes, comments: { ...comments, [code]: comment } })
        }
      />
    )}
    <Icd10CodePicker
      excludeCodes={codes}
      onSelect={(picked) =>
        onChange({
          codes: Array.from(new Set([...codes, picked.code])),
          comments,
        })
      }
      disabled={disabled}
      placeholder="Search or type an ICD-10 code"
      emptyMessage="No ICD-10 codes found"
    />
  </div>
);

export const Icd10CodesList = ({ codes, comments }: Icd10Answer) => {
  if (codes.length === 0) {
    return <div className="text-body text-foreground-muted">—</div>;
  }
  return <Icd10CodeRows codes={codes} comments={comments} />;
};
